import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

const ACTIVATE_DELAY = 0;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    // Handle outside clicks to close drawer
    document.addEventListener('click', this.#handleOutsideClick, {
      signal: this.#abortController.signal,
    });

    // Desktop drawer: level-two item click (show sub-menu) and back button
    document.addEventListener('click', this.#handleDesktopDrawerContentClick, {
      signal: this.#abortController.signal,
    });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#debouncedActivateHandler.cancel();

    this.#debouncedActivateHandler(event);
  };

  /**
   * Activate the selected menu item with a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    let overflowMenuHeight = this.overflowMenu?.offsetHeight ?? 0;

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = submenu ? Math.max(submenu.offsetHeight, overflowMenuHeight) : 0;

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');
  };

  #debouncedActivateHandler = debounce(this.#activateHandler, ACTIVATE_DELAY);

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    this.#debouncedActivateHandler.cancel();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the debounce before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  /**
   * Handle click on menu items with children - open drawer.
   * Top-level links without dropdowns (no data-has-children) are not handled here:
   * they use default link behavior and navigate to their URL (fallback to menu links).
   * @param {MouseEvent} event
   */
  #handleMenuItemClick = (event) => {
    if (!(event.target instanceof Element)) return;

    // Find the link element - could be the link itself or a child element
    let link = /** @type {HTMLElement} */ (event.target);
    
    // If clicking on a child element (like the span), find the parent link
    if (!link.hasAttribute('data-has-children')) {
      const closestLink = link.closest('[data-has-children="true"]') ||
                          link.closest('a[data-has-children="true"]');
      if (closestLink instanceof HTMLElement) {
        link = closestLink;
      }
    }
    
    // Only open drawer for items with dropdowns; others fall back to normal link navigation
    if (!link || !link.hasAttribute('data-has-children')) {
      return;
    }

    // Only handle on desktop (mobile uses hamburger menu)
    if (window.innerWidth < 750) return;

    event.preventDefault();
    event.stopPropagation();

    const linkHandle = link.getAttribute('data-link-handle');
    if (!linkHandle) return;

    this.#openDrawerForMenuItem(linkHandle, link);
  };

  /**
   * Handle clicks inside the desktop drawer: level-two items (show sub-menu) and back button.
   * Uses document delegation so it runs regardless of DOM/script order.
   * @param {MouseEvent} event
   */
  #handleDesktopDrawerContentClick = (event) => {
    if (window.innerWidth < 750) return;

    const wrapper = this.#getDesktopDrawer();
    if (!wrapper || !(event.target instanceof Node) || !wrapper.contains(event.target)) return;

    const backButton = event.target instanceof Element ? event.target.closest('.menu-drawer__level-three-panel .menu-drawer__back-button') : null;
    if (backButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#desktopDrawerBackToLevelTwo(backButton);
      return;
    }

    const levelTwoButton = event.target instanceof Element ? event.target.closest('.menu-drawer__menu-item--has-children') : null;
    if (levelTwoButton instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#desktopDrawerShowLevelThree(levelTwoButton);
    }
  };

  /**
   * Show level-three panel for a level-two button in the desktop drawer.
   * @param {HTMLElement} button - The .menu-drawer__menu-item--has-children button
   */
  #desktopDrawerShowLevelThree(button) {
    const targetId = button.getAttribute('data-target-column');
    if (!targetId) return;

    let layout = button.closest('.menu-drawer__three-column-layout');
    if (!layout) {
      const submenu = button.closest('.menu-drawer__submenu');
      layout = submenu ? submenu.querySelector('.menu-drawer__three-column-layout') : null;
    }
    if (!layout) return;

    const targetPanel = layout.querySelector(`#${CSS.escape(targetId)}`);
    if (!targetPanel) return;

    const isCurrentlyActive = !targetPanel.hasAttribute('hidden');

    const allPanels = layout.querySelectorAll('.menu-drawer__level-three-panel');
    const allButtons = layout.querySelectorAll('.menu-drawer__menu-item--has-children');
    allPanels.forEach((panel) => panel.setAttribute('hidden', ''));
    allButtons.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

    if (isCurrentlyActive) {
      layout.classList.remove('has-level-three');
    } else {
      targetPanel.removeAttribute('hidden');
      button.setAttribute('aria-expanded', 'true');
      layout.classList.add('has-level-three');
      if (layout instanceof HTMLElement) void layout.offsetHeight;
    }
  }

  /**
   * Desktop drawer: back from level-three to level-two.
   * @param {HTMLElement} backButton - The .menu-drawer__back-button element
   */
  #desktopDrawerBackToLevelTwo(backButton) {
    const levelThreePanel = backButton.closest('.menu-drawer__level-three-panel');
    if (!levelThreePanel) return;

    const layout = levelThreePanel.closest('.menu-drawer__three-column-layout');
    if (!layout) return;

    layout.querySelectorAll('.menu-drawer__level-three-panel').forEach((panel) => panel.setAttribute('hidden', ''));
    layout.querySelectorAll('.menu-drawer__menu-item--has-children').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
    layout.classList.remove('has-level-three');
  }

  /**
   * Handle clicks outside the menu/drawer to close drawer
   * @param {MouseEvent} event
   */
  #handleOutsideClick = (event) => {
    if (window.innerWidth < 750) return;

    const wrapper = this.#getDesktopDrawer();
    if (!wrapper) return;

    if (!wrapper.querySelector('details[open]')) return;

    const menuContainer = this.closest('header-menu');
    const clickedElement = event.target instanceof Node ? event.target : null;

    if (
      (menuContainer && clickedElement && menuContainer.contains(clickedElement)) ||
      (clickedElement && wrapper.contains(clickedElement))
    ) {
      return;
    }

    this.#closeDesktopPanel(wrapper);
  };

  /**
   * Open drawer and navigate to specific menu item's submenu
   * @param {string} linkHandle - The handle of the menu link
   * @param {HTMLElement} linkElement - The link element that was clicked
   */
  #openDrawerForMenuItem(linkHandle, linkElement) {
    const wrapper = this.#getDesktopDrawer();
    if (!wrapper) return;

    // Independent desktop: one <details> per panel; open that details directly (no shared drawer).
    const targetDetails = wrapper.querySelector(`#Details-menu-drawer-${linkHandle}-desktop`);
    if (!targetDetails) return;

    if (targetDetails.hasAttribute('open')) {
      this.#closeDesktopPanel(wrapper);
      return;
    }

    wrapper.querySelectorAll('details[open]').forEach((d) => {
      d.removeAttribute('open');
      d.classList.remove('menu-open');
    });
    targetDetails.setAttribute('open', '');
    targetDetails.classList.add('menu-open');
    wrapper.classList.add('has-open');

    const menuDrawer = targetDetails.querySelector('.menu-drawer');
    if (menuDrawer) {
      menuDrawer.classList.add('menu-drawer--animating-in');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          menuDrawer.classList.remove('menu-drawer--animating-in');
        });
      });
    }
  }

  /**
   * Get the desktop drawer component
   * @returns {HTMLElement | null}
   */
  #getDesktopDrawer() {
    // Independent desktop: one wrapper with one <details> per panel
    const wrapper = document.querySelector('.header__drawer--desktop');
    return wrapper instanceof HTMLElement ? wrapper : null;
  }

  /**
   * Close the open desktop panel (independent structure).
   * @param {HTMLElement} wrapper - The .header__drawer--desktop wrapper
   */
  #closeDesktopPanel(wrapper) {
    const openDetails = wrapper.querySelector('details[open]');
    if (!openDetails) return;

    const menuDrawer = openDetails.querySelector('.menu-drawer');
    if (!menuDrawer) {
      openDetails.removeAttribute('open');
      openDetails.classList.remove('menu-open');
      wrapper.classList.remove('has-open');
      return;
    }

    let done = false;
    const finishClose = () => {
      if (done) return;
      done = true;
      menuDrawer.removeEventListener('transitionend', onTransitionEnd);
      openDetails.removeAttribute('open');
      openDetails.classList.remove('menu-open');
      wrapper.classList.remove('has-open');
      menuDrawer.classList.remove('menu-drawer--animating-out');
    };

    /** @param {TransitionEvent} e */
    const onTransitionEnd = (e) => {
      if (e.target !== menuDrawer || e.propertyName !== 'transform') return;
      finishClose();
    };

    menuDrawer.addEventListener('transitionend', onTransitionEnd);
    requestAnimationFrame(() => {
      menuDrawer.classList.add('menu-drawer--animating-out');
    });

    const duration = 250;
    setTimeout(finishClose, duration + 50);
  }

  /**
   * Public method to handle menu item clicks (called from template)
   * @param {MouseEvent} event
   */
  handleMenuItemClick = (event) => {
    this.#handleMenuItemClick(event);
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element?.matches('[slot="more"')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  return element?.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
